import React, { useEffect, useState } from 'react';
import { ChevronRight, Megaphone } from 'lucide-react';
import { useAnnouncements } from '../../context/AnnouncementContext';
import { levelOf } from './announcementLevels';
import './announcements.css';

const ROTATE_MS = 6000;

const firstLine = (text) => (text || '').split('\n').find((l) => l.trim()) || '';

// Sits in every app header (MainLayout and the tile layouts) between the
// title and the user menu.
const AnnouncementBanner = () => {
  const { enabled, loaded, announcements, bannerItems, openModal } = useAnnouncements();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const count = bannerItems.length;
  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [count, index]);

  if (!enabled || !loaded) return <div className="flex-1" />;

  const current = bannerItems[index] || bannerItems[0];

  if (!current) {
    // Nothing to shout about — leave a quiet way into the history.
    return (
      <div className="flex-1 min-w-0 flex justify-end px-4">
        {announcements.length > 0 && (
          <button
            type="button"
            onClick={() => openModal()}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-slate-400
                       hover:text-slate-100 hover:bg-slate-700/60 transition-colors"
            title="Past announcements"
          >
            <Megaphone className="w-4 h-4" />
            <span className="hidden md:inline">Announcements</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-[7rem] flex justify-center px-2 sm:px-4">
      <BannerPill
        item={current}
        isUnseen={!current.viewed_at}
        moreCount={count - 1}
        paused={paused}
        onOpen={() => openModal(current.id)}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        progressKey={count > 1 ? `${current.id}-${index}` : null}
        onProgressEnd={() => setIndex((i) => (i + 1) % count)}
      />
    </div>
  );
};

// The banner's look, shared with the admin editor's live preview.
export const BannerPill = ({
  item, isUnseen, moreCount = 0, paused = false, onOpen,
  onMouseEnter, onMouseLeave, progressKey = null, onProgressEnd,
}) => {
  const lvl = levelOf(item.level);
  const { Icon } = lvl;
  const preview = firstLine(item.body);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (onOpen && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpen();
        }
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`group relative overflow-hidden w-full max-w-3xl h-10 rounded-full border cursor-pointer
        bg-gradient-to-r ${lvl.banner} ksp-ann-shimmer ksp-ann-arrive ${paused ? 'ksp-ann-paused' : ''}
        shadow-lg shadow-black/20 hover:brightness-110 transition-[filter]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60`}
      title="Click to read"
    >
      <div key={item.id} className="ksp-ann-slide-in relative z-10 flex items-center h-full pl-1.5 pr-2 gap-2.5">
        <span className={`relative flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${lvl.iconWrap}`}>
          {isUnseen && <span className={`absolute inset-0 rounded-full animate-ping opacity-60 ${lvl.ping}`} />}
          <Icon className="w-4 h-4 relative" />
        </span>

        {isUnseen ? (
          <span className="hidden sm:inline flex-shrink-0 px-1.5 py-0.5 rounded bg-white text-[10px] font-extrabold tracking-wider text-slate-900 uppercase">
            New
          </span>
        ) : (
          <span className="hidden sm:inline flex-shrink-0 text-[10px] font-bold tracking-wider uppercase opacity-80">
            {lvl.label}
          </span>
        )}

        <span className="min-w-0 truncate text-sm">
          <span className="font-semibold">{item.title}</span>
          {preview && <span className="hidden lg:inline opacity-75"> — {preview}</span>}
        </span>

        <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
          {moreCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-black/30 border border-white/20 text-xs font-semibold whitespace-nowrap">
              +{moreCount} more
            </span>
          )}
          <span className="hidden md:flex items-center text-xs font-medium opacity-70 group-hover:opacity-100 transition-opacity">
            Read <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </span>
      </div>

      {progressKey && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 z-10">
          <div
            key={progressKey}
            className={`ksp-ann-progress h-full ${lvl.bar}`}
            style={{ animationDuration: `${ROTATE_MS}ms` }}
            onAnimationEnd={onProgressEnd}
          />
        </div>
      )}
    </div>
  );
};

export default AnnouncementBanner;
